/**
 * @fileoverview Theme toggle component for switching between light/dark/system modes.
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMonitor } from 'react-icons/fi';
import { useThemeStore, type ThemeMode } from '@/lib/stores/themeStore';
import { cn } from '@/lib/utils/cn';

const THEME_OPTIONS: Array<{ mode: ThemeMode; icon: typeof FiSun; label: string }> = [
  { mode: 'light', icon: FiSun, label: 'Light' },
  { mode: 'dark', icon: FiMoon, label: 'Dark' },
  { mode: 'system', icon: FiMonitor, label: 'System' },
];

/**
 * Theme toggle dropdown component.
 */
export function ThemeToggle() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { mode, setMode } = useThemeStore();

  // Get current icon
  const CurrentIcon = THEME_OPTIONS.find((o) => o.mode === mode)?.icon ?? FiMonitor;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (newMode: ThemeMode) => {
    setMode(newMode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-center w-9 h-9 rounded-lg transition-all',
          'hover:bg-accent text-muted-foreground hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          isOpen && 'bg-accent text-foreground'
        )}
        aria-label={`Theme: ${mode}`}
        aria-expanded={isOpen}
      >
        <CurrentIcon className="h-[18px] w-[18px]" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 p-1 bg-background rounded-lg border shadow-lg z-50 min-w-[120px] backdrop-blur-xl"
          >
            {THEME_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = mode === option.mode;

              return (
                <button
                  key={option.mode}
                  onClick={() => handleSelect(option.mode)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors',
                    'hover:bg-accent',
                    isSelected
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{option.label}</span>
                  {isSelected && (
                    <motion.div
                      layoutId="theme-indicator"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-primary"
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
